import React, { useState } from 'react';
import { SearchFilterType } from '../../services/api/paymentsApi';

interface FilterBarProps {
  onApplyFilters: (filters: SearchFilterType) => void;
  initialFilters?: SearchFilterType;
}

const FilterBar: React.FC<FilterBarProps> = ({ onApplyFilters, initialFilters = {} }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [filters, setFilters] = useState<SearchFilterType>({
    paymentTrackingUISearchType: 'totalPaymentsProcessed',
    startSearchDateRange: today,
    endSearchDateRange: today,
    paymentType: 'outbound',
    ...initialFilters
  });

  // Helper function to update a filter value
  const updateFilter = (key: keyof SearchFilterType, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onApplyFilters(filters);
  };

  // Reset filters
  const handleReset = () => {
    const resetFilters: SearchFilterType = {
      paymentTrackingUISearchType: 'totalPaymentsProcessed',
      startSearchDateRange: today,
      endSearchDateRange: today,
      paymentType: 'outbound'
    };
    setFilters(resetFilters);
    onApplyFilters(resetFilters);
  };

  return (
    <div style={{
      backgroundColor: '#f8f9fa',
      padding: '16px',
      borderRadius: '4px',
      marginBottom: '24px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
    }}>
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' }}>
          {/* Date Range */}
          <div>
            <label htmlFor="startDate" style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
              Start Date
            </label>
            <input
              id="startDate"
              type="date"
              value={filters.startSearchDateRange || today}
              onChange={(e) => updateFilter('startSearchDateRange', e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label htmlFor="endDate" style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
              End Date
            </label>
            <input
              id="endDate"
              type="date"
              value={filters.endSearchDateRange || today}
              onChange={(e) => updateFilter('endSearchDateRange', e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          {/* Payment Type */}
          <div>
            <label htmlFor="paymentType" style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
              Direction
            </label>
            <select
              id="paymentType"
              value={filters.paymentType || 'outbound'}
              onChange={(e) => updateFilter('paymentType', e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '120px'
              }}
            >
              <option value="outbound">Outbound</option>
              <option value="inbound">Inbound</option>
            </select>
          </div>

          {/* Currency */}
          <div>
            <label htmlFor="currency" style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
              Currency
            </label>
            <select
              id="currency"
              value={filters.currency || ''}
              onChange={(e) => updateFilter('currency', e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '120px'
              }}
            >
              <option value="">All Currencies</option>
              <option value="GBP">GBP</option>
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
            </select>
          </div>

          {/* Payment Method / Scheme */}
          <div>
            <label htmlFor="scheme" style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
              Payment Method
            </label>
            <select
              id="scheme"
              value={filters.scheme || ''}
              onChange={(e) => updateFilter('scheme', e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '120px'
              }}
            >
              <option value="">All Methods</option>
              <option value="SWIFT">SWIFT</option>
              <option value="CHAPS">CHAPS</option>
              <option value="FPS">FPS</option>
              <option value="BACS">BACS</option>
            </select>
          </div>

          {/* Business Area */}
          <div>
            <label htmlFor="businessArea" style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
              Business Area
            </label>
            <select
              id="businessArea"
              value={filters.businessArea || ''}
              onChange={(e) => updateFilter('businessArea', e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '150px'
              }}
            >
              <option value="">All Business Areas</option>
              <option value="PrivateClientsUK">Private Clients UK</option>
              <option value="PrivateCompaniesGroup">Private Companies Group</option>
            </select>
          </div>

          {/* Buttons */}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={handleReset}
              style={{
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Reset
            </button>
            <button
              type="submit"
              style={{
                backgroundColor: '#003366',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Apply Filters
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default FilterBar; 